import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Building2, Search } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import PageHeader from "../../components/system/PageHeader";
import Card from "../../components/system/Card";
import Button from "../../components/system/Button";
import EmptyState from "../../components/system/EmptyState";
import Input, { Select } from "../../components/system/Input";
import TenantStatusPill, { TENANT_STATUSES } from "./TenantStatusPill";

/**
 * Operator-only list of every tenant company.
 *
 * - Lists Companies (cross-tenant — only platform_superadmin can read this).
 * - Filter by status; client-side filter by name/slug substring.
 * - Click a row → /platform/companies/:cid (detail).
 * - "New tenant" CTA → /platform/companies/new.
 */
export default function TenantsListPage() {
  const nav = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const { data } = await api.get("/platform/companies", { params });
      setCompanies(data.companies || []);
    } catch (e) {
      const msg = formatApiErrorDetail(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Reload on status filter change
  useEffect(() => {
    load();
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.slug || "").toLowerCase().includes(q) ||
        (c.id || "").toLowerCase().includes(q),
    );
  }, [companies, query]);

  return (
    <div data-testid="platform-tenants-list-page">
      <PageHeader
        eyebrow="Control Center"
        title="Tenants"
        subtitle="Every customer company on ShelfLife. Click a row to inspect, change status, or activate."
        actions={
          <Button
            tier="primary"
            onClick={() => nav("/platform/companies/new")}
            data-testid="new-tenant-button"
          >
            <Plus size={16} /> New tenant
          </Button>
        }
      />

      <Card variant="raised" className="p-4 mb-4">
        <div className="flex gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <Input
              variant="search"
              placeholder="Search by name, slug or id…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="tenants-search-input"
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="tenants-status-filter"
            >
              <option value="">All statuses</option>
              {TENANT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </Card>

      {loading ? (
        <Card variant="raised" className="p-10 text-center">
          <div
            className="font-mono text-sm text-muted-foreground"
            data-testid="tenants-loading"
          >
            Loading tenants…
          </div>
        </Card>
      ) : error ? (
        <EmptyState
          icon={<Search size={36} />}
          title="Could not load tenants"
          description={error}
          action={
            <Button tier="secondary" onClick={load} data-testid="tenants-retry">
              Retry
            </Button>
          }
          data-testid="tenants-error"
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Building2 size={36} />}
          title={
            companies.length === 0
              ? "No tenants yet"
              : "No tenants match your search"
          }
          description={
            companies.length === 0
              ? "Create the first customer company to begin."
              : "Try clearing your filter or search term."
          }
          action={
            companies.length === 0 ? (
              <Button
                tier="primary"
                onClick={() => nav("/platform/companies/new")}
                data-testid="tenants-empty-create"
              >
                <Plus size={16} /> New tenant
              </Button>
            ) : null
          }
          data-testid="tenants-empty"
        />
      ) : (
        <Card variant="raised" className="overflow-hidden">
          <div className="hidden md:grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr] gap-4 px-4 py-3 bg-paper-sunk border-b border-border/60 data-sku">
            <div>Name</div>
            <div>Slug</div>
            <div>Status</div>
            <div>Created</div>
            <div className="text-right">Actions</div>
          </div>
          {filtered.map((c, idx) => (
            <div
              key={c.id}
              className={`md:grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr] gap-4 px-4 py-4 items-center ${
                idx > 0 ? "border-t border-border/40" : ""
              }`}
              data-testid={`tenant-row-${c.id}`}
            >
              <button
                onClick={() => nav(`/platform/companies/${c.id}`)}
                className="flex items-center gap-3 text-left hover:text-brand transition-colors"
                data-testid={`tenant-name-${c.id}`}
              >
                <Building2
                  size={18}
                  className="text-muted-foreground shrink-0"
                />
                <span className="font-display font-semibold truncate">
                  {c.name || "—"}
                </span>
              </button>
              <div
                className="font-mono text-xs"
                data-testid={`tenant-slug-${c.id}`}
              >
                {c.slug || (
                  <span className="text-muted-foreground">—</span>
                )}
              </div>
              <div>
                <TenantStatusPill
                  status={c.status}
                  data-testid={`tenant-status-${c.id}`}
                />
              </div>
              <div
                className="font-mono text-[11px] text-muted-foreground"
                data-testid={`tenant-created-${c.id}`}
              >
                {(c.created_at || "").slice(0, 10) || "—"}
              </div>
              <div className="mt-2 md:mt-0 md:text-right">
                <Button
                  tier="secondary"
                  size="sm"
                  onClick={() => nav(`/platform/companies/${c.id}`)}
                  data-testid={`tenant-open-${c.id}`}
                >
                  Open
                </Button>
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
